import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BookingDetails from '../../components/Booking/BookingDetails';
import RestUtilities from '../../services/RestUtilities';

import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { loadingState, toastState } from '../../state/RecoilState';
import { useSetRecoilState } from 'recoil';
import FeesDialogue from '../../components/Booking/FeesDialogue';
import { Booking, BookingComment } from '../../@types/api';
import PaymentsDialogue from '../../components/Booking/PaymentsDialogue';
import dayjs from 'dayjs';
import { bookingStatus, dbDayFormat, dbTimeFormat } from '../../Constants';

const feeTypes = {
  BOOKINGFEE: 1,
  ARTISTFEE: 2,
  SHOWCASEDEPOSIT: 3,
};

const sxProps = {
  tableContainer: {
    marginBottom: 1,
    marginTop: 1,
  },
  buttonGroup: {
    marginY: 2,
  },
};

const EditBooking: React.FC = () => {
  const { id } = useParams();
  const setLoading = useSetRecoilState(loadingState);
  const [booking, setBooking] = useState<Booking | undefined>(undefined);
  const [bookingComment, setBookingComment] = useState<string>('');
  const [openFeesDialog, setOpenFeesDialog] = useState(false);
  const [openPaymentsDialog, setOpenPaymentsDialog] = useState(false);
  const [feeTypeId, setFeeTypeId] = useState<1 | 2 | 3>(1);
  const [feeAmount, setFeeAmount] = useState<string>('208.33');
  const [rateId, setRateId] = useState<1 | 2 | 3>(3);
  const [paymentBookingFeeId, setPaymentBookingFeeId] = useState<number>(-1);
  const [paymentMethodId, setPaymentMethodId] = useState<1 | 3 | 4>(3);
  const [paymentAmount, setPaymentAmount] = useState<number>(250);
  const setToast = useSetRecoilState(toastState);
  const clientName =
    booking?.client?.clientNames[booking.client.clientNames.length - 1];
  const clientTelephone =
    booking?.client?.clientTelephones[
      booking.client.clientTelephones.length - 1
    ];
  const hasFees =
    booking?.bookingFees &&
    booking.bookingFees.some((x) => x.feeTypeId === feeTypes.BOOKINGFEE) &&
    booking.bookingFees.some((x) => x.feeTypeId === feeTypes.ARTISTFEE);
  const hasArtistFees =
    booking?.bookingFees &&
    booking.bookingFees.some((x) => x.feeTypeId === feeTypes.ARTISTFEE);
  const clientContractSent = booking?.isContractSigned;
  const artistBookingFormSent = booking?.isBandContractSigned;

  const setBookingSafely = (data: Booking) => {
    setBooking({
      ...data,
      eventDate: dayjs(data.eventDate),
      entStartTime: dayjs(data.entStartTime),
      entEndTime: dayjs(data.entEndTime),
    } as Booking);
  };

  useEffect(() => {
    const getBooking = async () => {
      setLoading(true);
      const res = await RestUtilities.get(`/api/booking/Get/${id}`);
      if (res.ok) {
        var data: Booking = res.content;
        setBookingSafely(data);
        setBookingComment(data.bookingComment?.comments);
        setToast('Booking loaded');
      }
      setLoading(false);
    };
    if (id) {
      getBooking();
    }
  }, [id, setLoading, setToast]);

  const saveChanges = async (updatedBooking: Booking) => {
    if (!booking) return;
    const bookingModel: Booking = {
      ...updatedBooking,
      bookingComment: {
        bookingId: booking.bookingId,
        comments: bookingComment,
      } as BookingComment,
    };
    if (
      bookingModel.statusId !== bookingStatus.CANCELLED &&
      bookingModel.statusId !== bookingStatus.CONFIRMED
    ) {
      const isArtistBookedRes = await RestUtilities.get(
        `api/booking/IsArtistBooked?artistId=${
          updatedBooking.artistId
        }&eventDate=${dayjs(updatedBooking.eventDate).format(dbDayFormat)}`
      );
      if (isArtistBookedRes.ok) {
        var isArtistBooked = isArtistBookedRes.content;
        if (isArtistBooked === true) {
          setToast('This artist is already booked on this date');
          return;
        }
      } else {
        setToast('Error occurred checking artist availability');
        return;
      }
    }
    const data = {
      ...bookingModel,
      eventDate: bookingModel.eventDate.format(dbDayFormat),
      entStartTime: bookingModel.entStartTime.format(dbTimeFormat),
      entEndTime: bookingModel.entEndTime.format(dbTimeFormat),
    };
    const res = await RestUtilities.put('api/booking/put', data);
    if (res.ok) {
      const dbUpdated = res.content;
      setBookingSafely(dbUpdated);
      setBookingComment(data.bookingComment?.comments);
      setToast('Booking Updated');
    }
  };

  const openTab = (route: string) => {
    const isUpdate =
      route === 'ArtistBookingForm'
        ? artistBookingFormSent
        : clientContractSent;
    const newPageUrl = `/api/email/${route}/${id}?format=html&isUpdate=${isUpdate}`;
    window.open(newPageUrl, '_blank'); //to open new page
  };

  const sendEmail = async (
    route:
      | 'SendArtistBookingForm'
      | 'SendClientContract'
      | 'SendClientInvoice'
      | 'SendFollowUp'
  ) => {
    const isUpdate =
      route === 'SendArtistBookingForm'
        ? artistBookingFormSent
        : clientContractSent;
    const url = `/api/email/${route}/${id}?isUpdate=${isUpdate}`;
    const res = await RestUtilities.get(url);
    if (res.ok) {
      const data: Booking = await res.content;
      setBookingSafely(data);
      setToast('Email was sent successfully');
    } else {
      setToast('Email failed');
    }
  };

  return (
    <Box>
      {booking && (
        <Box sx={{ pt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'en'}>
            <Typography variant='h5' color='primary'>
              Edit BookingID: {booking?.bookingId}
            </Typography>
            <Grid container spacing={2}>
              <Grid item sm={12} lg={6}>
                <Box
                  display='flex'
                  alignItems='flex-start'
                  justifyContent='center'
                >
                  <Box sx={{ width: '50%' }}>
                    <Typography
                      color='textSecondary'
                      variant='h6'
                      component='h3'
                    >
                      {clientName?.firstName} {clientName?.surname}
                    </Typography>
                    <Typography variant='body2'>
                      ClientID: {booking?.client?.clientId}
                    </Typography>
                    <Typography variant='body2'>
                      {clientTelephone?.telephoneNumber}
                    </Typography>
                    <Typography variant='body2'>
                      <a href={`mailto:${booking.client?.emailAddress}`}>
                        {booking.client?.emailAddress}
                      </a>
                    </Typography>
                  </Box>
                  <Box sx={{ width: '50%', ml: 2, mt: 2 }}>
                    <TextField
                      multiline
                      variant='outlined'
                      id='bookingComment'
                      label='MFS Notes'
                      value={bookingComment || ''}
                      onChange={(event) =>
                        setBookingComment(event.target.value)
                      }
                      fullWidth
                      InputProps={{
                        rows: 2,
                        multiline: true,
                        inputComponent: 'textarea',
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ pt: 2 }}>
                  <BookingDetails
                    booking={booking}
                    saveChanges={saveChanges}
                    buttonText='Update Booking'
                  />
                </Box>
                {hasFees && (
                  <Box
                    sx={sxProps.buttonGroup}
                    display='flex'
                    justifyContent='space-between'
                  >
                    <Button
                      size='small'
                      startIcon={<SearchIcon />}
                      onClick={() => openTab('ClientContract')}
                      color='secondary'
                      variant='contained'
                    >
                      View Contract
                    </Button>
                    <Button
                      size='small'
                      startIcon={<SearchIcon />}
                      onClick={() => openTab('ArtistBookingForm')}
                      color='secondary'
                      variant='contained'
                    >
                      View BookingForm
                    </Button>
                    <Button
                      size='small'
                      startIcon={<SearchIcon />}
                      onClick={() => openTab('ClientInvoice')}
                      color='secondary'
                      variant='contained'
                    >
                      View Invoice
                    </Button>
                    <Button
                      size='small'
                      startIcon={<SearchIcon />}
                      onClick={() => openTab('ContractFollowUp')}
                      color='secondary'
                      variant='contained'
                    >
                      View FollowUp
                    </Button>
                  </Box>
                )}
                {hasArtistFees && (
                  <Box
                    sx={sxProps.buttonGroup}
                    display='flex'
                    justifyContent='space-between'
                  >
                    <Button
                      size='small'
                      startIcon={<SendIcon />}
                      onClick={() => sendEmail('SendClientContract')}
                      color='warning'
                      variant='contained'
                    >
                      Send Contract
                    </Button>
                    <Button
                      size='small'
                      startIcon={<SendIcon />}
                      onClick={() => sendEmail('SendArtistBookingForm')}
                      color='warning'
                      variant='contained'
                    >
                      Send BookingForm
                    </Button>
                    <Button
                      size='small'
                      startIcon={<SendIcon />}
                      onClick={() => sendEmail('SendClientInvoice')}
                      color='warning'
                      variant='contained'
                    >
                      Send Invoice
                    </Button>
                    {clientContractSent && (
                      <Button
                        size='small'
                        startIcon={<SendIcon />}
                        onClick={() => sendEmail('SendFollowUp')}
                        color='warning'
                        variant='contained'
                      >
                        Send FollowUp
                      </Button>
                    )}
                  </Box>
                )}
              </Grid>
              <Grid item sm={12} lg={6}>
                <Box className='emailLogs'>
                  <Typography color='secondary' variant='body2'>
                    Email Logs
                  </Typography>
                  <TableContainer component={Paper} sx={sxProps.tableContainer}>
                    <Table aria-label='emails table' size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>EmailType</TableCell>
                          <TableCell align='right'>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {booking.emailLogs?.map((row) => (
                          <TableRow key={row.logId}>
                            <TableCell>
                              {row.emailType.emailTypeDescription}
                            </TableCell>
                            <TableCell align='right'>
                              {dayjs(row.sentWhen).format('DD MMM YYYY')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box sx={{ pt: 2 }} className='fees'>
                  <Button
                    size='small'
                    disableElevation
                    variant='contained'
                    onClick={() => {
                      setOpenFeesDialog(true);
                    }}
                    color='secondary'
                  >
                    Add Fee
                  </Button>
                  <FeesDialogue
                    id={id}
                    setBookingSafely={setBookingSafely}
                    setOpen={setOpenFeesDialog}
                    feeTypeId={feeTypeId}
                    setFeeTypeId={setFeeTypeId}
                    feeAmount={feeAmount}
                    setFeeAmount={setFeeAmount}
                    rateId={rateId}
                    setRateId={setRateId}
                    booking={booking}
                    open={openFeesDialog}
                    location={booking.location}
                  />
                </Box>
                <Box sx={{ pt: 2 }}>
                  <Button
                    size='small'
                    disableElevation
                    variant='contained'
                    onClick={() => {
                      setOpenPaymentsDialog(true);
                    }}
                    color='secondary'
                  >
                    Add Payment
                  </Button>
                  <PaymentsDialogue
                    id={id}
                    open={openPaymentsDialog}
                    setOpen={setOpenPaymentsDialog}
                    paymentBookingFeeId={paymentBookingFeeId}
                    setPaymentBookingFeeId={setPaymentBookingFeeId}
                    paymentMethodId={paymentMethodId}
                    setPaymentMethodId={setPaymentMethodId}
                    paymentAmount={paymentAmount}
                    setPaymentAmount={setPaymentAmount}
                    booking={booking}
                    setBookingSafely={setBookingSafely}
                  />
                </Box>
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      )}
    </Box>
  );
};
export default EditBooking;
